import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

export type CategoryKind = "income" | "expense";

/**
 * One node of the two-level tree (`model.md` §3.4).
 *
 * Children arrive nested rather than flat with a parent id, because every
 * consumer — the picker, and later the budget screen and the report roll-up —
 * wants the tree and would otherwise rebuild it identically and separately.
 */
export interface Category {
  id: string;
  parent_id?: string;
  depth: number;
  name: string;
  kind: CategoryKind;
  icon?: string;
  color?: string;
  is_archived: boolean;
  sort_order: number;
  template_key?: string;
  children?: Category[];
}

/** The small, bounded collection envelope (`ListResponse[T]` on the server). */
interface List<T> {
  items: T[];
}

export const categoriesQueryKey = (householdId: string, includeArchived = false) =>
  ["households", householdId, "categories", includeArchived] as const;

/**
 * Fetches the tree.
 *
 * Archived categories are excluded by default, which is the difference between
 * a picker and a report: an archived category still appears in every historical
 * figure and must never appear in a list of things to choose (`model.md` §6.6).
 */
export function useCategories(householdId: string | undefined, includeArchived = false) {
  return useQuery({
    queryKey: categoriesQueryKey(householdId ?? "", includeArchived),
    queryFn: ({ signal }) => {
      const query = includeArchived ? "?include_archived=true" : "";
      return apiFetch<List<Category>>(
        `/api/v1/households/${householdId}/categories${query}`,
        { signal },
      );
    },
    enabled: Boolean(householdId),
  });
}

export interface CreateCategoryInput {
  name: string;
  kind?: CategoryKind;
  /** Omit for a top-level category. A subcategory inherits its parent's kind. */
  parent_id?: string;
}

export function useCreateCategory(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCategoryInput) =>
      apiFetch<Category>(`/api/v1/households/${householdId}/categories`, {
        method: "POST",
        body: input,
      }),
    // Returned, not discarded: react-query waits for it before the mutation
    // settles, so a caller that navigates on success arrives at a list that
    // already has the new row.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["households", householdId, "categories"] }),
  });
}

export function useArchiveCategory(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      apiFetch<Category>(`/api/v1/households/${householdId}/categories/${id}/archived`, {
        method: "PUT",
        body: { is_archived: archived },
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["households", householdId, "categories"] }),
  });
}

/**
 * Flattens the tree into picker options, children indented under their parent.
 *
 * A parent is offered as well as its children: `model.md` §4.2 rolls a parent up
 * from its descendants *and* whatever is filed directly against it, so "Wohnen"
 * with no subcategory chosen is a legitimate answer rather than a half-finished
 * one.
 */
export function pickerOptions(tree: Category[]): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  for (const parent of tree) {
    out.push({ id: parent.id, label: parent.name });
    for (const child of parent.children ?? []) {
      // Two non-breaking spaces, written as escapes because they are
      // otherwise invisible in the source: a reader cannot tell an indent from
      // a stray character, and a `select` collapses ordinary spaces away.
      out.push({ id: child.id, label: `\u00a0\u00a0${child.name}` });
    }
  }
  return out;
}
